import { ReactNode } from 'react'
import { Button, Group, Loader, Stack, Text, Title } from '@common/ui'
import { useNavigate } from 'react-router-dom'
import ConfirmationDialog from '../../../../components/layout/ConfirmationDialog'
import AuditLogDrawer from '../../../audit/AuditLogDrawer'
import { useImpersonation } from '../../../../io/context/ImpersonationContext'

type PageLayoutProps = {
  title: string
  subtitle?: string
  children: ReactNode
  actions?: ReactNode
  auditTargetId?: string
  showBack?: boolean
  showCancel?: boolean
  showSave?: boolean
  showDelete?: boolean
  saveDisabled?: boolean
  submitting?: boolean
  onSave?: () => void
  onDelete?: () => void
}

const PageLayout = ({
  title,
  subtitle,
  children,
  actions,
  auditTargetId,
  showBack,
  showCancel,
  showSave,
  showDelete,
  saveDisabled,
  submitting,
  onSave,
  onDelete,
}: PageLayoutProps) => {
  const navigate = useNavigate()
  const { isReadOnly } = useImpersonation()
  const writesAllowed = !isReadOnly

  return (
    <Stack gap="lg" p="lg" flex={1} style={{ minHeight: 0 }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" style={{ flexShrink: 0 }}>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Title order={2} data-testid="entity-page-title">
            {title}
          </Title>
          {subtitle && (
            <Text c="dimmed" data-testid="entity-page-subtitle">
              {subtitle}
            </Text>
          )}
        </Stack>
        {(actions || auditTargetId) && (
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }} data-testid="entity-page-actions">
            {auditTargetId && <AuditLogDrawer targetId={auditTargetId} />}
            {actions}
          </Group>
        )}
      </Group>
      <Stack gap={0} flex={1} style={{ minHeight: 0 }}>
        {children}
      </Stack>
      <Group gap="lg" justify="flex-end" style={{ flexShrink: 0 }}>
        {showBack && (
          <Button variant="subtle" onClick={() => navigate(-1)} data-testid="entity-action-back">
            Back
          </Button>
        )}
        {showCancel && (
          <Button
            variant="subtle"
            onClick={() => navigate(-1)}
            disabled={submitting}
            data-testid="entity-action-cancel"
          >
            Cancel
          </Button>
        )}
        {showDelete && onDelete && writesAllowed && (
          <ConfirmationDialog
            title={`There may still be references to this entity in the system. Are you sure you want to
                      delete this entity? This action cannot be undone.`}
            content={<Text>Delete</Text>}
            onConfirm={onDelete}
            triggerTestId="entity-action-delete"
            cancelTestId="entity-action-delete-cancel"
            confirmTestId="entity-action-delete-confirm"
            dialogTestId="entity-action-delete-dialog"
            disabled={submitting}
          />
        )}
        {showSave && onSave && writesAllowed && (
          <Button
            onClick={onSave}
            disabled={saveDisabled || submitting}
            leftSection={submitting ? <Loader size="xs" color="white" /> : undefined}
            data-testid="entity-action-save"
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        )}
      </Group>
    </Stack>
  )
}

export default PageLayout
